import React, { useState, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAllSubscribers } from "../../firebase";
import SearchIcon from "@mui/icons-material/Search";
import EmailIcon from "@mui/icons-material/Email";

interface Subscriber {
  id: string;
  email: string;
  status: string;
  subscribedAt: string;
}

const SubscribersManagement: React.FC = () => {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [filteredSubscribers, setFilteredSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  // Email functionality
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailData, setEmailData] = useState({
    title: "",
    message: "",
  });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);

  // Load all subscribers from Firestore
  useEffect(() => {
    const loadSubscribers = async () => {
      try {
        setLoading(true);
        const allSubscribers = await getAllSubscribers();
        setSubscribers(allSubscribers);
        setFilteredSubscribers(allSubscribers);
      } catch (err: any) {
        console.error("Error loading subscribers:", err);
        setError(err.message || "Failed to load subscribers");
      } finally {
        setLoading(false);
      }
    };

    loadSubscribers();
  }, []);

  // Filter subscribers based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredSubscribers(subscribers);
    } else {
      const lowercasedFilter = searchTerm.toLowerCase();
      const filtered = subscribers.filter((subscriber) => {
        return (
          subscriber.email.toLowerCase().includes(lowercasedFilter) ||
          subscriber.status.toLowerCase().includes(lowercasedFilter)
        );
      });
      setFilteredSubscribers(filtered);
      setPage(0); // Reset to first page when filtering
    }
  }, [searchTerm, subscribers]);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Email functionality
  const handleOpenEmailDialog = () => {
    setEmailData({
      title: "",
      message: ``,
    });
    setEmailError(null);
    setEmailSuccess(null);
    setEmailDialogOpen(true);
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
  };

  const handleSendEmail = async () => {
    if (!emailData.title.trim() || !emailData.message.trim()) {
      setEmailError("Please provide both a title and message.");
      return;
    }

    // Get email addresses of active subscribers only
    const activeSubscriberEmails = subscribers
      .filter(subscriber => subscriber.status === "active" && subscriber.email)
      .map(subscriber => subscriber.email)
      .filter(email => email && email.trim() !== "");

    if (activeSubscriberEmails.length === 0) {
      setEmailError("No active subscribers found with valid email addresses.");
      return;
    }

    setEmailLoading(true);
    setEmailError(null);
    setEmailSuccess(null);

    try {
      const functions = getFunctions();
      const sendGeneralEmailFunction = httpsCallable(functions, 'sendGeneralEmailFunction');
      
      const result = await sendGeneralEmailFunction({
        title: emailData.title,
        message: emailData.message,
        emailAddresses: activeSubscriberEmails
      });

      setEmailSuccess(`Email sent successfully to ${activeSubscriberEmails.length} active subscribers!`);
      
      // Close dialog after a short delay
      setTimeout(() => {
        setEmailDialogOpen(false);
        setEmailSuccess(null);
      }, 2000);

    } catch (error: any) {
      console.error("Error sending email:", error);
      setEmailError(error.message || "Failed to send email. Please try again.");
    } finally {
      setEmailLoading(false);
    }
  };

  // Calculate pagination
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedSubscribers = filteredSubscribers.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredSubscribers.length / rowsPerPage);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 mb-4">Error: {error}</div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-gold text-white rounded hover:bg-gold/90"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg">
      {/* Search and email section */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={handleOpenEmailDialog}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gold hover:bg-gold/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold"
          >
            <EmailIcon className="h-4 w-4 mr-2" />
            Email Subscribers
          </button>
        </div>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SearchIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by email or status..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
          />
        </div>
      </div>

      {/* Subscribers table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Subscribed Date
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedSubscribers.map((subscriber) => (
              <tr key={subscriber.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {subscriber.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      subscriber.status === "active"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {subscriber.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(subscriber.subscribedAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => handleChangePage(page - 1)}
            disabled={page === 0}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => handleChangePage(page + 1)}
            disabled={page >= totalPages - 1}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing{" "}
              <span className="font-medium">{startIndex + 1}</span> to{" "}
              <span className="font-medium">
                {Math.min(endIndex, filteredSubscribers.length)}
              </span>{" "}
              of <span className="font-medium">{filteredSubscribers.length}</span>{" "}
              results
            </p>
          </div>
          <div>
            <nav
              className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
              aria-label="Pagination"
            >
              <button
                onClick={() => handleChangePage(page - 1)}
                disabled={page === 0}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handleChangePage(page + 1)}
                disabled={page >= totalPages - 1}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Rows per page selector */}
      <div className="bg-white px-4 py-3 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <label htmlFor="rows-per-page" className="text-sm text-gray-700 mr-2">
              Rows per page:
            </label>
            <select
              id="rows-per-page"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Email Dialog */}
      {emailDialogOpen && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-start">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Email Subscribers
                  </h3>
                  <button
                    type="button"
                    onClick={handleCloseEmailDialog}
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="mt-4">
                  {emailLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gold"></div>
                    </div>
                  ) : (
                    <>
                      {emailError && (
                        <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-md">
                          {emailError}
                        </div>
                      )}
                      {emailSuccess && (
                        <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-md">
                          {emailSuccess}
                        </div>
                      )}
                      <div>
                        <label
                          htmlFor="email-title"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Email Title
                        </label>
                        <input
                          type="text"
                          id="email-title"
                          value={emailData.title}
                          onChange={(e) =>
                            setEmailData({
                              ...emailData,
                              title: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md shadow-sm border-gray-300 focus:ring-gold focus:border-gold text-base py-3 px-4"
                          placeholder="Enter email title..."
                        />
                      </div>
                      <div className="mt-6">
                        <label
                          htmlFor="email-message"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Email Message
                        </label>
                        <textarea
                          id="email-message"
                          value={emailData.message}
                          onChange={(e) =>
                            setEmailData({
                              ...emailData,
                              message: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md shadow-sm border-gray-300 focus:ring-gold focus:border-gold text-base py-3 px-4 min-h-[300px] resize-y"
                          placeholder="Enter email message here..."
                        />
                        <p className="mt-2 text-sm text-gray-500">
                          You can use line breaks to format your message. The email will be sent to all active subscribers.
                        </p>
                      </div>
                      <div className="mt-6">
                        <button
                          type="button"
                          onClick={handleSendEmail}
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gold text-base font-medium text-white hover:bg-gold/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold sm:w-auto sm:text-sm"
                        >
                          Send Email
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscribersManagement; 